import axios from 'axios';

// Use Netlify Functions in production, local API in development
const isProduction = import.meta.env.PROD;
const API_BASE_URL = isProduction 
  ? '/.netlify/functions' 
  : (import.meta.env.VITE_API_URL || 'http://localhost:8000');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 25000, // 25 second timeout
});

// Add retry logic with exponential backoff
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // Start with 1 second

api.interceptors.request.use(config => {
  config.metadata = { startTime: new Date() };
  config.retryCount = config.retryCount || 0;
  return config;
});

// Add response interceptor for error handling and retries
api.interceptors.response.use(
  response => {
    const duration = new Date() - response.config.metadata.startTime;
    console.log(`%c✅ [API] Request completed in ${duration}ms`, 'color: #4CAF50');
    return response;
  },
  async error => {
    const config = error.config;
    
    // Check if we should retry
    if (config && config.retryCount < MAX_RETRIES) {
      // Retry on timeout (ECONNABORTED) or 5xx errors
      if (error.code === 'ECONNABORTED' || 
          error.code === 'ERR_NETWORK' ||
          (error.response && error.response.status >= 500)) {
        
        config.retryCount = (config.retryCount || 0) + 1;
        const delay = RETRY_DELAY * Math.pow(2, config.retryCount - 1); // Exponential backoff
        
        console.warn(`%c⚠️ [API] Request failed, retrying (${config.retryCount}/${MAX_RETRIES}) after ${delay}ms...`, 
                     'color: #FF9800', error.message);
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Retry the request
        return api(config);
      }
    }
    
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// Agency endpoints
export const fetchAgencies = async () => {
  console.log('%c🔷 [API] fetchAgencies called', 'color: #2196F3; font-weight: bold');
  
  // Load from real data files
  try {
    console.log('%c📦 [API] Loading real agencies from data files', 'color: #4CAF50');
    const response = await fetch('/data/agencies.json');
    if (!response.ok) throw new Error('Failed to load agencies');
    
    const agencies = await response.json();
    console.log('%c✅ [API] Loaded', 'color: #4CAF50', agencies.length, 'real agencies');
    return agencies;
  } catch (error) {
    console.error('%c❌ [API] Failed to load real agencies:', 'color: #F44336', error);
    
    // Fallback to hardcoded if file load fails
    console.log('%c📦 [API] Using fallback agencies', 'color: #FF9800');
    const agencies = [
      { id: "dcr", name: "Department of Conservation and Recreation", acronym: "DCR" },
      { id: "vdh", name: "Virginia Department of Health", acronym: "VDH" },
      { id: "doe", name: "Department of Education", acronym: "DOE" },
      { id: "dcjs", name: "Department of Criminal Justice Services", acronym: "DCJS" },
      { id: "dbhds", name: "Department of Behavioral Health and Developmental Services", acronym: "DBHDS" },
      { id: "dss", name: "Department of Social Services", acronym: "DSS" },
      { id: "vdacs", name: "Department of Agriculture and Consumer Services", acronym: "VDACS" },
      { id: "dmas", name: "Department of Medical Assistance Services", acronym: "DMAS" },
    ];
    return agencies;
  }
};

export const fetchAgency = async (agencyId) => {
  const response = await api.get(`/agencies/${agencyId}`);
  return response.data;
};

// Regulation endpoints
export const fetchRegulations = async (params = {}) => {
  console.log('%c🔷 [API] fetchRegulations called with params:', 'color: #2196F3; font-weight: bold', params);
  
  // Load from real data files
  try {
    console.log('%c📦 [API] Loading real regulations from data files', 'color: #4CAF50');
    
    // Load analyzed regulations only
    const response = await fetch('/data/analyzed_regulations.json');
    if (!response.ok) throw new Error('Failed to load regulations');
    const regulations = await response.json();
    
    console.log('%c✅ [API] Loaded', 'color: #4CAF50', regulations.length, 'real regulations');
    
    // Load agency-specific provisions data to get discretionary requirements counts
    // Try to load provisions for each agency that has regulations
    const agencyIds = [...new Set(regulations.map(r => r.agencyId))];
    
    for (const agencyId of agencyIds) {
      try {
        // Try agency-specific provisions file first
        const provisionsResponse = await fetch(`/data/${agencyId}_provisions.json`);
        if (provisionsResponse.ok) {
          const provisionsData = await provisionsResponse.json();
          if (provisionsData.agencies && provisionsData.agencies.length > 0) {
            const agencyData = provisionsData.agencies[0];
            // Merge discretionary requirements data into regulations
            agencyData.regulations.forEach(provReg => {
              const mainReg = regulations.find(r => r.id === provReg.id);
              if (mainReg) {
                mainReg.discretionary_requirements_total = provReg.discretionary_requirements_total || 0;
                mainReg.discretionary_requirements_summary = provReg.discretionary_requirements_summary || '';
                
                // Extract authorizing statute from provisions if not already set
                if (!mainReg.authorizingStatute && provReg.provisions && provReg.provisions.length > 0) {
                  // Count occurrences of each statute
                  const statuteCounts = {};
                  provReg.provisions.forEach(prov => {
                    const statute = prov.authorizing_statute || prov.statutory_authority;
                    if (statute) {
                      statuteCounts[statute] = (statuteCounts[statute] || 0) + 1;
                    }
                  });
                  
                  // Find the most common statute
                  let mostCommonStatute = '';
                  let maxCount = 0;
                  for (const [statute, count] of Object.entries(statuteCounts)) {
                    if (count > maxCount) {
                      maxCount = count;
                      mostCommonStatute = statute;
                    }
                  }
                  
                  mainReg.authorizingStatute = mostCommonStatute;
                }
              }
            });
            console.log(`%c✅ [API] Merged ${agencyId} discretionary requirements data`, 'color: #4CAF50');
          }
        }
      } catch (error) {
        // Silently skip if agency provisions file doesn't exist
        console.log(`%c⚠️ [API] No provisions file for ${agencyId}`, 'color: #FF9800');
      }
    }
    
    // Filter by params if provided
    let filtered = regulations;
    if (params.agency_ids) {
      const agencyList = params.agency_ids.split(',');
      filtered = filtered.filter(r => agencyList.includes(r.agencyId));
    }
    if (params.search) {
      const searchLower = params.search.toLowerCase();
      filtered = filtered.filter(r => 
        r.title.toLowerCase().includes(searchLower) ||
        r.number.toLowerCase().includes(searchLower) ||
        (r.text && r.text.toLowerCase().includes(searchLower))
      );
    }
    
    console.log('%c✅ [API] Returning', 'color: #4CAF50', filtered.length, 'filtered regulations');
    return filtered;
  } catch (error) {
    console.error('%c❌ [API] Failed to load real regulations:', 'color: #F44336', error);
    
    if (!isProduction) {
      console.log('%c🌐 [API] Trying backend API', 'color: #2196F3');
      try {
        const response = await api.get('/regulations', { params });
        return response.data.regulations || [];
      } catch (backendError) {
        console.error('%c❌ [API] Backend also failed:', 'color: #F44336', backendError);
      }
    }
    
    // Return empty if all fails
    return [];
  }
};

function getAgencyTitle(agencyId) {
  const titles = {
    'dcr': 'Conservation and Recreation',
    'vdh': 'Health',
    'doe': 'Education',
    'dcjs': 'Criminal Justice',
    'dbhds': 'Behavioral Health',
    'dss': 'Social Services',
    'vdacs': 'Agriculture',
    'dmas': 'Medical Assistance',
  };
  return titles[agencyId] || 'General';
}

export const fetchRegulation = async (regulationId) => {
  const response = await api.get(`/regulations/${regulationId}`);
  return response.data;
};

// AI Analysis through Netlify Functions
export const analyzeProvisions = async (regulationId, data) => {
  if (isProduction) {
    // Use Netlify AI function
    const response = await api.post('/ai-analysis', {
      analysisType: 'provisions',
      regulationText: data.text || data.regulation_text,
      userId: 'user', // You can get this from auth context
    });
    return response.data.result;
  } else {
    const response = await api.post(`/regulations/${regulationId}/provisions`, data);
    return response.data;
  }
};

export const generateCBA = async (regulationId, data) => {
  if (isProduction) {
    // Use modular Netlify function for CBA generation
    const response = await api.post('/generate-cba-modular', {
      regulation_id: regulationId,
      regulation_text: data.regulation_text || data.text,
      user_context: data.user_context || null
    });
    return response.data;
  } else {
    // Use local backend for development
    const response = await api.post(`/generate-cba`, {
      regulation_id: regulationId,
      regulation_text: data.regulation_text || data.text,
      user_context: data.user_context || null
    });
    return response.data;
  }
};

// Regulation Rewrite endpoint
export const rewriteRegulation = async (regulationText, provisionsAnalysis = [], userContext = null) => {
  if (isProduction) {
    // Use Netlify function for rewrite
    const response = await api.post('/rewrite-regulation', {
      regulation_text: regulationText,
      provisions_analysis: provisionsAnalysis,
      user_context: userContext
    });
    return response.data;
  } else {
    // Use local backend for development
    const response = await api.post('/rewrite-regulation', {
      regulation_text: regulationText,
      provisions_analysis: provisionsAnalysis,
      user_context: userContext
    });
    return response.data;
  }
};

// Consolidation endpoint
export const consolidateRegulations = async (regulations, userContext = null) => {
  if (isProduction) {
    // Use Netlify function for consolidation
    const response = await api.post('/consolidate-regulations', {
      regulations,
      user_context: userContext
    });
    return response.data;
  } else {
    // Use local backend for development
    const response = await api.post('/consolidate-regulations', {
      regulations,
      user_context: userContext
    });
    return response.data;
  }
};

// Bulk CBA endpoint
export const generateBulkCBA = async (regulations, userContext = null) => {
  if (isProduction) {
    // Use Netlify function for bulk CBA
    const response = await api.post('/bulk-cba', {
      regulations,
      user_context: userContext
    });
    return response.data;
  } else {
    // Use local backend for development
    const response = await api.post('/bulk-cba', {
      regulations,
      user_context: userContext
    });
    return response.data;
  }
};

// Statute matching endpoint
export const matchStatutes = async (regulationText, provisions = []) => {
  if (isProduction) {
    // Use Netlify AI function
    const response = await api.post('/ai-analysis', {
      analysisType: 'statute_matching',
      regulationText: regulationText,
      provisions: provisions,
      userId: 'user',
    });
    return response.data.result;
  } else {
    const response = await api.post('/match-statutes', regulationText);
    return response.data;
  }
};

// Mock data generator for development
export const generateMockRegulation = (agencyId, index) => {
  const agencies = {
    'dcr': 'Conservation and Recreation',
    'vdh': 'Health',
    'doe': 'Education',
    'dcjs': 'Criminal Justice',
    'dbhds': 'Behavioral Health',
  };

  return {
    id: `${agencyId}_reg_${index}`,
    number: `1VAC${75 + index}-${20 + index}-${10 + index * 10}`,
    title: `${agencies[agencyId] || 'General'} Regulation ${index}`,
    agencyId: agencyId,
    authorizingStatute: `§ 10.1-${1300 + index}`,
    discretionaryPercentage: Math.floor(Math.random() * 40) + 50,
    effectiveDate: '2024-01-01',
    lastUpdated: '2024-06-01',
    text: generateMockRegulationText(index),
  };
};

const generateMockRegulationText = (index) => {
  return `
    Section ${index}.1 - Purpose
    This regulation establishes standards and procedures for the implementation of state programs under the authority of § 10.1-${1300 + index}.

    Section ${index}.2 - Definitions
    The following words and terms when used in this chapter shall have the following meanings unless the context clearly indicates otherwise:
    "Agency" means the responsible state agency.
    "Applicant" means any person seeking approval under this regulation.
    "Director" means the agency director or designee.

    Section ${index}.3 - General Requirements
    A. All persons subject to this regulation shall comply with the following requirements:
    1. Submit applications in the prescribed format
    2. Maintain records for a minimum of three years
    3. Allow agency inspection upon request

    B. The director may grant exceptions to these requirements upon showing of good cause.

    Section ${index}.4 - Discretionary Provisions
    The agency may, in its discretion:
    1. Extend deadlines for good cause shown
    2. Waive certain requirements for small entities
    3. Approve alternative compliance methods

    Section ${index}.5 - Enforcement
    Violations of this regulation may result in:
    1. Written warnings
    2. Civil penalties not to exceed $10,000
    3. Permit revocation or suspension
  `.trim();
};

// Mock provisions data
export const generateMockProvisions = (regulationId) => {
  return [
    {
      id: `${regulationId}_prov_1`,
      section: '1',
      text: 'This regulation establishes standards and procedures for implementation.',
      type: 'mandatory',
      is_discretionary: false,
      authorizing_statute: '§ 10.1-1300',
      analysis: 'This provision sets forth the regulatory framework and is mandatory in nature.',
    },
    {
      id: `${regulationId}_prov_2`,
      section: '2',
      text: 'The director may grant exceptions upon showing of good cause.',
      type: 'discretionary',
      is_discretionary: true,
      authorizing_statute: '§ 10.1-1301',
      analysis: 'This provision grants discretionary authority to the director for exception handling.',
    },
    {
      id: `${regulationId}_prov_3`,
      section: '3',
      text: 'All persons shall submit applications in the prescribed format.',
      type: 'mandatory',
      is_discretionary: false,
      authorizing_statute: '§ 10.1-1302',
      analysis: 'This is a mandatory procedural requirement for all applicants.',
    },
  ];
};

// Load provisions data
export const fetchProvisions = async () => {
  console.log('%c🔷 [API] fetchProvisions called', 'color: #2196F3; font-weight: bold');
  
  try {
    // Start with empty provisions map
    const provisionsByRegulation = {};
    
    // Try loading old provisions.json first (as fallback)
    try {
      console.log('%c📦 [API] Loading legacy provisions from data files', 'color: #4CAF50');
      const response = await fetch('/data/provisions.json');
      if (response.ok) {
        const provisions = await response.json();
        console.log('%c✅ [API] Loaded', 'color: #4CAF50', provisions.length, 'legacy provisions');
        
        // Group provisions by regulationId for easier access
        provisions.forEach(provision => {
          if (!provisionsByRegulation[provision.regulationId]) {
            provisionsByRegulation[provision.regulationId] = [];
          }
          provisionsByRegulation[provision.regulationId].push(provision);
        });
      }
    } catch (error) {
      console.log('%c⚠️ [API] No legacy provisions.json found, using agency-specific files only', 'color: #FF9800');
    }
    
    // Load agency-specific three-bucket provisions (these take priority)
    const agencyFiles = ['treasury', 'doe', 'dpor', 'dcjs', 'vdh', 'dss', 'deq', 'dmas', 'dbhds', 'vdacs', 'health'];
    
    for (const agency of agencyFiles) {
      try {
        const agencyResponse = await fetch(`/data/${agency}_provisions.json`);
        if (agencyResponse.ok) {
          const agencyData = await agencyResponse.json();
          console.log(`%c🏛️ [API] Loading ${agency} three-bucket provisions`, 'color: #9C27B0');
          
          if (agencyData.agencies && agencyData.agencies.length > 0) {
            const agencyProvisions = agencyData.agencies[0];
            
            // Map agency three-bucket provisions to the standard format
            // IMPORTANT: Three-bucket data takes priority over legacy data
            agencyProvisions.regulations.forEach(reg => {
              if (reg.provisions && reg.provisions.length > 0) {
                // Replace any existing provisions with the three-bucket data
                provisionsByRegulation[reg.id] = reg.provisions.map(prov => ({
                  ...prov,
                  // Use authorization field for three-bucket authLevel
                  authLevel: prov.authorization || prov.authLevel,
                  regulationId: reg.id,
                  isThreeBucket: true, // Flag to identify three-bucket provisions
                  // Map display text for UI based on authorization field
                  displayAuthLevel: (prov.authorization || prov.authLevel) === 'MANDATORY' ? 'Mandatory Requirement' :
                                   (prov.authorization || prov.authLevel) === 'AUTHORIZED' ? 'Discretionary Authority' :
                                   (prov.authorization || prov.authLevel) === 'UNAUTHORIZED' ? 'Unauthorized' :
                                   (prov.authorization || prov.authLevel)
                }));
              }
            });
            console.log(`%c✅ [API] Loaded ${agency} three-bucket provisions for`, 'color: #4CAF50', 
                       agencyProvisions.regulations.length, 'regulations');
          }
        }
      } catch (e) {
        // Silently skip if agency provisions file doesn't exist
      }
    }
    
    console.log('%c✅ [API] Total provisions for', 'color: #4CAF50', Object.keys(provisionsByRegulation).length, 'regulations');
    return provisionsByRegulation;
  } catch (error) {
    console.error('%c❌ [API] Failed to load real provisions:', 'color: #F44336', error);
    return {};
  }
};

// Mock CBA data
export const generateMockCBA = (regulationId) => {
  const costs = Math.floor(Math.random() * 500000) + 100000;
  const benefits = costs * (1.2 + Math.random() * 0.8);
  
  return {
    regulation_id: regulationId,
    summary: {
      total_costs: costs,
      total_benefits: benefits,
      net_benefit: benefits - costs,
      benefit_cost_ratio: (benefits / costs).toFixed(2),
    },
    costs: [
      {
        category: 'Compliance',
        amount: costs * 0.6,
        description: 'Direct compliance costs for regulated entities',
      },
      {
        category: 'Administrative',
        amount: costs * 0.25,
        description: 'Administrative and paperwork burden',
      },
      {
        category: 'Training',
        amount: costs * 0.15,
        description: 'Staff training and education costs',
      },
    ],
    benefits: [
      {
        category: 'Public Health',
        amount: benefits * 0.4,
        description: 'Improved public health outcomes',
      },
      {
        category: 'Environmental',
        amount: benefits * 0.35,
        description: 'Environmental protection benefits',
      },
      {
        category: 'Economic',
        amount: benefits * 0.25,
        description: 'Economic efficiency gains',
      },
    ],
    timeframe: '10 years',
    discount_rate: '3%',
    generated_at: new Date().toISOString(),
  };
};

export default api;